import React from "react";
import Seo from "../components/Seo.jsx";

/* Opportunities feed — filters, sort, density, density-aware rendering. */

const SORTS = [
  { id: "best", label: "Best match" },
  { id: "new", label: "Newest" },
  { id: "discussed", label: "Most discussed" },
];

const SEARCH_SCOPES = [
  { id: "all", label: "All results" },
  { id: "post", label: "Posts only" },
  { id: "comment", label: "Comments only" },
];

function normalizeSearchScope(scope) {
  if (scope === "post" || scope === "posts") return "post";
  if (scope === "comment" || scope === "comments") return "comment";
  return "all";
}

function ChipSelect({ label, value, options, onChange, active }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const selected = options.find((o) => o.id === value);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className={"chip-select " + (active ? "is-active " : "")} onClick={() => setOpen((v) => !v)}>
        <span className="chip-select-label">{label}</span>
        <span className="chip-select-value">{selected?.label ?? "—"}</span>
        <window.I.Chevron size={12} style={{ color: "var(--text-tertiary)" }} />
      </button>
      {open && (
        <div className="dropdown-menu">
          {options.map((o) => (
            <div
              key={o.id}
              className={"item " + (o.id === value ? "is-selected" : "")}
              onClick={() => { onChange(o.id); setOpen(false); }}
            >
              <span className="check">{o.id === value ? <window.I.Check size={14} /> : null}</span>
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OpportunitiesScreen({ state, setState, onOpenOpp, onFeedback }) {
  const {
    opps, oppsLoading, density, scoreMode, sort, threshold,
    highOnly, query, subredditFilter, hideNotRelevant, searchScope, isScanning,
    oppsUpdatedAt, onRefreshOpps,
  } = state;
  const planState = (window.usePlan ? window.usePlan() : null) || { loading: true, plan: "free", limits: { max_visible_opportunities: 15 } };
  const maxVisible = planState?.loading ? null : planState?.limits?.max_visible_opportunities;
  const goPricing = () => {
    try { window.history.pushState({}, "", "/pricing"); window.dispatchEvent(new PopStateEvent("popstate")); }
    catch (e) { window.location.assign("/pricing"); }
  };
  const [scopeOpen, setScopeOpen] = React.useState(false);
  const scopeRef = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => {
      if (scopeRef.current && !scopeRef.current.contains(e.target)) setScopeOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const subreddits = React.useMemo(() => {
    const set = new Set(opps.map((o) => o.subreddit));
    return ["all", ...Array.from(set)];
  }, [opps]);

  const normalizedSearchScope = normalizeSearchScope(searchScope);
  const selectedScope = SEARCH_SCOPES.find((scope) => scope.id === normalizedSearchScope) || SEARCH_SCOPES[0];
  const hasOpps = opps.length > 0;
  const showFeedSkeleton = oppsLoading && !hasOpps;

  const filtered = React.useMemo(() => {
    let list = opps.filter((o) => o.score >= threshold);
    if (highOnly) list = list.filter((o) => o.tier === "high");
    if (subredditFilter !== "all") list = list.filter((o) => o.subreddit === subredditFilter);
    if (hideNotRelevant) list = list.filter((o) => o.feedback !== "not_relevant");
    if (normalizedSearchScope !== "all") list = list.filter((o) => o.kind === normalizedSearchScope);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((o) =>
        o.title.toLowerCase().includes(q) ||
        o.why.toLowerCase().includes(q) ||
        o.subreddit.toLowerCase().includes(q)
      );
    }

    // Cluster comment opportunities by parent thread BEFORE sorting, so a
    // thread cluster sorts as a single row using its top member's score.
    // Posts stay solo. Threads with a single matching comment are not
    // clustered (would be a redundant container).
    // In the "Comments only" view we skip clustering so every matched
    // comment shows as its own expanded card rather than a collapsed
    // thread container.
    if (normalizedSearchScope !== "comment") {
      list = window.clusterOpps ? window.clusterOpps(list) : list;
    }

    if (sort === "best") list = [...list].sort((a, b) => b.score - a.score);
    if (sort === "new") list = [...list].sort((a, b) => parseTime(a.posted) - parseTime(b.posted));
    if (sort === "discussed") list = [...list].sort((a, b) => b.comments - a.comments);
    return list;
  }, [opps, threshold, highOnly, subredditFilter, hideNotRelevant, normalizedSearchScope, query, sort]);

  const filteredOppCount = React.useMemo(
    () => (window.countOpportunities ? window.countOpportunities(filtered) : filtered.length),
    [filtered]
  );

  function parseTime(p) {
    // 3h, 6h, 1d, 2d ago
    const m = p.match(/(\d+)([hd])/);
    if (!m) return 0;
    const n = parseInt(m[1], 10);
    return m[2] === "h" ? n : n * 24;
  }

  const setKey = (k) => (v) => setState((s) => ({ ...s, [k]: v }));

  return (
    <div className="page screen-enter" style={{ maxWidth: 1080 }}>
      <Seo title="Opportunities" noindex />
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {isScanning ? (
              <span className="scan-live-label scan-live-label-hero" aria-label="Scanning">
                <span className="scan-live-label-dot" />
                Scanning...
              </span>
            ) : "Opportunities"}
          </h1>
          <p className="page-subtitle">
            {isScanning
              ? "Scanning in the background. Existing opportunities stay visible while new matches come in."
              : "Relevant conversations from your last scan. Open each one to review context before engaging."}
          </p>
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-input">
          <window.I.Search size={14} />
          <input
            placeholder="Search…"
            value={query}
            onChange={(e) => setKey("query")(e.target.value)}
          />
          <div ref={scopeRef} style={{ position: "relative" }}>
            <button
              type="button"
              className={"search-filter-btn " + (normalizedSearchScope !== "all" ? "is-active" : "")}
              onClick={() => setScopeOpen((v) => !v)}
              title={selectedScope.label}
              aria-label="Filter search results by type"
              aria-haspopup="menu"
              aria-expanded={scopeOpen ? "true" : "false"}
            >
              <window.I.Filter size={13} />
            </button>
            {scopeOpen && (
              <div className="dropdown-menu search-filter-menu">
                {SEARCH_SCOPES.map((scope) => (
                  <div
                    key={scope.id}
                    className={"item " + (scope.id === normalizedSearchScope ? "is-selected" : "")}
                    onClick={() => {
                      setKey("searchScope")(scope.id);
                      setScopeOpen(false);
                    }}
                  >
                    <span className="check">{scope.id === normalizedSearchScope ? <window.I.Check size={14} /> : null}</span>
                    {scope.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {query && (
            <button className="btn-ghost" onClick={() => setKey("query")("")} style={{ display: "grid", placeItems: "center", width: 18, height: 18, borderRadius: 4 }}>
              <window.I.X size={12} />
            </button>
          )}
        </div>

        <ChipSelect
          label="Sort"
          value={sort}
          options={SORTS}
          onChange={setKey("sort")}
        />

        <ChipSelect
          label="Subreddit"
          value={subredditFilter}
          active={subredditFilter !== "all"}
          options={subreddits.map((s) => ({ id: s, label: s === "all" ? "All subreddits" : s }))}
          onChange={setKey("subredditFilter")}
        />

        <ChipSelect
          label="Min score"
          value={String(threshold)}
          active={threshold > 60}
          options={[
            { id: "60", label: "≥ 60%" },
            { id: "75", label: "≥ 75%" },
            { id: "85", label: "≥ 85%" },
            { id: "90", label: "≥ 90%" },
          ]}
          onChange={(v) => setKey("threshold")(parseInt(v, 10))}
        />

        <button
          className={"chip-select " + (highOnly ? "is-active" : "")}
          onClick={() => setKey("highOnly")(!highOnly)}
        >
          <window.I.ShieldCheck size={12} />
          High confidence only
        </button>

        <button
          className={"chip-select " + (hideNotRelevant ? "is-active" : "")}
          onClick={() => setKey("hideNotRelevant")(!hideNotRelevant)}
          title="Hide opportunities you marked as not relevant"
        >
          <window.I.Eye size={12} />
          Hide dismissed
        </button>
      </div>

      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 4px 12px", fontSize: 12.5, color: "var(--text-tertiary)",
      }}>
        <div>
          Showing <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{filteredOppCount}</span> of {opps.length} opportunities
          {normalizedSearchScope !== "all" && <span> · {selectedScope.label}</span>}
          {threshold > 60 && <span> · min {threshold}% match</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {oppsUpdatedAt && (
            <span>
              Updated <window.RelativeUpdated at={oppsUpdatedAt} />
            </span>
          )}
          {onRefreshOpps && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => onRefreshOpps()}
              title="Refresh"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "2px 6px", borderRadius: 4,
                color: "var(--text-tertiary)",
              }}
            >
              <window.I.Refresh
                size={12}
                style={isScanning ? { animation: "spin 900ms linear infinite" } : null}
              />
              {isScanning ? "scanning..." : "Refresh"}
            </button>
          )}
        </div>
      </div>

      {showFeedSkeleton ? (
        <FeedSkeleton density={density} />
      ) : filtered.length === 0 ? (
        <EmptyFeed onReset={() => setState((s) => ({
          ...s,
          query: "", subredditFilter: "all", threshold: 0, highOnly: false, hideNotRelevant: false, searchScope: "all",
        }))} hasOpps={hasOpps} />
      ) : (
        <div className={"feed density-" + density}>
          {filtered.slice(0, maxVisible == null ? filtered.length : maxVisible).map((opp) => (
            opp.kind === "comment-cluster" ? (
              <window.ThreadClusterCard
                key={opp.id}
                opp={opp}
                onOpen={onOpenOpp}
                scoreMode={scoreMode}
              />
            ) : (
              <window.OpportunityCard
                key={opp.id}
                opp={opp}
                onOpen={onOpenOpp}
                onFeedback={onFeedback}
                onGenerate={onOpenOpp}
                scoreMode={scoreMode}
              />
            )
          ))}

          {maxVisible != null && filtered.length > maxVisible && (
            <>
              <LockedFeedBanner
                visibleCount={maxVisible}
                hiddenCount={filtered.length - maxVisible}
                onUpgrade={goPricing}
              />
              {/* Show the next 2 cards blurred so users see what they're missing. */}
              {filtered.slice(maxVisible, maxVisible + 2).map((opp) => {
                const inner = opp.kind === "comment-cluster" ? (
                  <window.ThreadClusterCard opp={opp} onOpen={goPricing} scoreMode={scoreMode} />
                ) : (
                  <window.OpportunityCard opp={opp} onOpen={goPricing} scoreMode={scoreMode} />
                );
                return (
                  <div
                    key={opp.id}
                    onClick={goPricing}
                    title="Upgrade to Solo to see this"
                    style={{ cursor: "pointer", position: "relative" }}
                  >
                    <div aria-hidden="true" style={{ filter: "blur(7px)", opacity: 0.65, pointerEvents: "none" }}>
                      {inner}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FeedSkeleton({ density }) {
  return (
    <div className={"feed density-" + density}>
      {[0, 1, 2, 3].map((i) => (
        <div className="opp-card" key={i}>
          <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
            <div className="skel" style={{ height: 11, width: 90 }} />
            <div className="skel" style={{ height: 11, width: 64 }} />
            <div className="skel" style={{ height: 11, width: 44 }} />
            <div style={{ marginLeft: "auto" }}>
              <div className="skel" style={{ height: 18, width: 92, borderRadius: 999 }} />
            </div>
          </div>
          <div className="skel" style={{ height: 16, width: "78%", marginBottom: 10 }} />
          <div className="skel" style={{ height: 12, width: "94%", marginBottom: 6 }} />
          <div className="skel" style={{ height: 12, width: "62%", marginBottom: 14 }} />
          <div style={{ display: "flex", gap: 12, paddingTop: 12, borderTop: "1px solid var(--border-subtle)" }}>
            <div className="skel" style={{ height: 11, width: 60 }} />
            <div className="skel" style={{ height: 11, width: 60 }} />
            <div className="skel" style={{ height: 11, width: 60 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function LockedFeedBanner({ visibleCount, hiddenCount, onUpgrade }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onUpgrade}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onUpgrade(); }}
      style={{
        padding: "22px 22px",
        borderRadius: 14,
        background: "linear-gradient(180deg, var(--bg-card) 0%, color-mix(in srgb, var(--accent-subtle) 70%, var(--bg-card)) 100%)",
        border: "1.5px solid var(--accent)",
        boxShadow: "0 18px 50px -22px color-mix(in srgb, var(--accent) 50%, transparent)",
        cursor: "pointer",
        display: "flex",
        gap: 16,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: "50%",
          background: "var(--accent-subtle)",
          color: "var(--accent-soft-text)",
          display: "grid",
          placeItems: "center",
          border: "1px solid var(--accent-border)",
          flexShrink: 0,
        }}
      >
        <window.I.Lock size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.3 }}>
          {hiddenCount} more {hiddenCount === 1 ? "opportunity" : "opportunities"} locked
        </div>
        <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.55, marginTop: 4 }}>
          Free plan shows the top {visibleCount} per scan. Solo unlocks every match, the 7-day window, and the daily digest.
        </div>
      </div>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={(e) => { e.stopPropagation(); onUpgrade(); }}
      >
        <window.I.Sparkles size={12} />
        Upgrade · $12.99/mo
      </button>
    </div>
  );
}

function EmptyFeed({ onReset, hasOpps }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">
        <window.I.Inbox size={20} />
      </div>
      <h3 className="empty-title">
        {hasOpps ? "No opportunities match these filters" : "No opportunities yet"}
      </h3>
      <p className="empty-desc">
        {hasOpps
          ? "Try lowering the minimum match score, clearing the search, or removing the subreddit filter."
          : "Your next scan runs in 22 hours. You can also run a scan manually from the top bar."}
      </p>
      {hasOpps && (
        <button className="btn btn-secondary btn-sm" onClick={onReset}>
          Reset filters
        </button>
      )}
    </div>
  );
}

window.OpportunitiesScreen = OpportunitiesScreen;
